import { DOCUMENT } from '@angular/common';
import {
    Component,
    EventEmitter,
    Inject,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    signal,
} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { LocationDTO } from '@core/dto/location';
import {
    DeviceModel,
    RebalancingModel,
} from '@core/models/rebalancing-model.model';
import { LocationService } from '@core/services/location.service';
import { RebalancesService } from '@core/services/rebalance.service';
import { formatNumberGroups } from '@core/utils/number-helpers';
import { FormControlFields } from '@core/utils/object-helpers';
import {
    AlertComponent,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
} from '@shared/components';
import { EIcons } from '@shared/models';
import { MaterialModule } from '@shared/modules';
import { NgxMaskDirective } from 'ngx-mask';
import { Subscription } from 'rxjs';
import { FirstStepForm } from '../interface/forms.interface';
import { NULL_AS_STRING } from 'src/app/constants/global.constants'
import { isLto } from 'src/environments/environment.utils';

@Component({
    selector: 'app-first-step',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MaterialModule,
        MatCardModule,
        NgxMaskDirective,
        ButtonComponent,
        IconButtonComponent,
        IconComponent,
    ],
    templateUrl: './first-step.component.html',
    styleUrl: './first-step.component.scss',
})
export class FirstStepComponent implements OnInit, OnDestroy {
    private readonly locationServices = inject(LocationService);
    private readonly rebalancingsServices = inject(RebalancesService);
    private readonly dialog = inject(MatDialog);

    readonly #subscriptions = new Subscription();
    readonly EIcons = EIcons;
    readonly NULL_AS_STRING = NULL_AS_STRING;
    readonly isLto = isLto;

    form: FormGroup<FormControlFields<FirstStepForm>>;
    @Input() rebalance: RebalancingModel | null = null;
    @Input() currentStep = signal<number>(-1);
    @Output() completeSave = new EventEmitter<any>();

    previewImage = signal<string | ArrayBuffer | null>('');
    file = signal<File | null>(null);

    hubs: Array<LocationDTO> = [];
    deviceList: Array<DeviceModel> = [];

    lastOdo: number | null = null;
    oldDate = new Date();
    currentTenant = signal<string>(sessionStorage.getItem('tenant')!);

    consentKmChangeCheckbox = new FormControl(false, []);

    constructor(@Inject(DOCUMENT) document: any) {
        this.form = new FormGroup<FormControlFields<FirstStepForm>>({
            hub: new FormControl(''),
            plate: new FormControl(''),
            vin: new FormControl(''),
            acountable: new FormControl('', {
                validators: [
                    Validators.required,
                    Validators.pattern('^[A-Za-zÀ-ÖØ-öø-ÿ\\s]+$'),
                ],
            }),
            date: new FormControl(new Date(), {
                validators: [Validators.required],
            }),
            hour: new FormControl('', { validators: [Validators.required] }),
            soc: new FormControl('', {
                validators: [
                    Validators.required,
                    Validators.min(0),
                    Validators.max(100),
                ],
            }),
            odo: new FormControl('', {
                validators: [
                    Validators.required,
                    Validators.min(0),
                    this.kmChangeValidator(this.consentKmChangeCheckbox),
                ],
            }),
            deviceId: new FormControl('', {
                validators: [Validators.required],
            }),
            accesories: new FormControl([], {
                validators: [Validators.required],
            }),
        });
    }

    ngOnInit(): void {
        this.getHubs();

        this.#subscriptions.add(
            this.consentKmChangeCheckbox.valueChanges.subscribe(() => {
                this.form.controls.odo.updateValueAndValidity();
            })
        );
        this.form.controls.hub.valueChanges.subscribe(() => {
            const number: any = this.form.controls.hub.value;
            this.rebalancingsServices
                .getDevices(number)
                .subscribe((devices) => {
                    this.deviceList = devices.devices;
                });
            });
        }

    setForm() {
        this.form.controls.plate?.enable();
        this.form.controls.vin?.enable();

        this.form.controls.hub?.patchValue(this.rebalance!.unit.id_location, {
            emitEvent: false,
        });
        this.form.controls.plate?.setValue(this.rebalance!.unit.plate);
        this.form.controls.vin?.setValue(this.rebalance!.unit.vin);
        this.lastOdo = this.rebalance!.unit.odo;
        this.form.controls.odo.setValue(this.rebalance!.unit?.odo!.toString());

        this.form.controls.plate?.disable();
        this.form.controls.vin?.disable();
        if (
            !isLto(this.currentTenant()) &&
            this.rebalance?.unit.id_location
        ) {
            this.form.controls.hub?.disable();
        }
    }

    ngOnDestroy(): void {
        this.#subscriptions.unsubscribe();
    }

    getHubs() {
        this.locationServices.getLocations().subscribe((data) => {
            this.hubs = data;
            this.getDevices();
        });
    }

    getDevices() {
        if (this.rebalance!.unit.id_location) {
            this.rebalancingsServices
                .getDevices(this.rebalance!.unit.id_location)
                .subscribe((devices) => {
                    this.deviceList = devices.devices;
                    this.setForm();
                });
        } else {
            this.setForm();
        }
    }

    private kmChangeValidator(checkControl: FormControl): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }
            const odo = this.convertInt(control.value.toString());
            if (this.lastOdo && odo < this.lastOdo! && !checkControl.value) {
                return { mustConsentKmChange: true };
            }
            return null;
        };
    }

    save() {
        let data: any;

        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        const date = new Date(this.form.value.date!);
        const [hh, mm] = this.form.value.hour!.split(':') as [string, string];
        date.setHours(parseInt(hh), parseInt(mm), 0, 0);

        const firstForm = this.form.value;
        delete firstForm.acountable;
        if (firstForm.deviceId === "null")
            firstForm.deviceId = null;

        data = {
            data: firstForm,
            step: 1,
            responsible: this.form.controls.acountable.value,
            comments: null,
        };
        this.form.disable();

        const extensionIndex = this.file()?.name.lastIndexOf('.');
        const extension = this.file()?.name.slice(extensionIndex);
        const fileName = `reb_${this.rebalance!.id}_${
            data.step + 1
        }_${new Date().getTime()}${extension}`;
        const newFile = new File([this.file()!], fileName, {
            type: this.file()?.type,
            lastModified: this.file()?.lastModified,
        });

        this.rebalancingsServices
            .sendRebalancingData(
                JSON.stringify(data),
                this.rebalance!.id,
                newFile
            )
            .subscribe({
                next: () => {
                    this.completeSave.emit();
                    this.dialog
                        .open(AlertComponent, {
                            data: {
                                type: 'success',
                                message: 'Rebalanceo guardado',
                                successButtonText: 'Aceptar',
                            },
                        })
                        .afterClosed()
                        .subscribe(() => {
                            this.currentStep.set(2);
                        });
                },
                error: (err) => {
                    console.log(err.error.error);
                },
            });
    }

    convertInt(str: string) {
        return Number(str.replace(/[.,]/g, ''));
    }

    firstFormHasError(control: string, error: string): boolean | undefined {
        return (
            this.form.get(control)?.touched &&
            this.form.get(control)?.hasError(error)
        );
    }

    parseFileToURL(file: File | any) {
        if (file.url) {
            this.previewImage.set(file.url);
            return;
        }

        return new Promise((resolve, reject) => {
            const lector = new FileReader();

            lector.onload = (event) => {
                this.previewImage.set(event.target!.result);
                resolve(event.target!.result);
            };

            lector.onerror = (error) => {
                console.log(error);
                reject(new Error('Error al leer el archivo.'));
            };

            lector.readAsDataURL(file);
        });
    }

    async setFiles(file: any, selector: any) {
        file.preventDefault();

        if (file.target.files[0]) {
            this.file.set(file.target.files[0]);
        }

        await this.parseFileToURL(this.file());

        selector.value = '';
    }

    protected readonly formatKm = formatNumberGroups;
}
